import React from 'react';
import PropTypes from 'prop-types';
import PriceRange from 'components/price-range/price-range';
import { Accordion, AccordionItem } from 'tradera-components/accordion';
import { FilterCategories } from './categories/filter-categories';
import { AttributeFilter } from './attributes/attribute-filter';
import { RadioSwitchFilter } from './radio-switch-filter';
import { useTranslator } from 'tradera-lang/translate';
import { getTranslatedAttributeName } from 'tradera-components/attributes/attribute-helper';
import { getAttributeIcon } from './icon-helper';
import FilterShipTo from 'tradera-apps/tradera/components/discover/components/filters/filter-ship-to';
import ItemTypeIcon from '@tradera/blueprint/theme/icons2/auction.svg?jsx';
import ItemStatusIcon from '@tradera/blueprint/theme/icons2/bids.svg?jsx';
import SellerTypeIcon from '@tradera/blueprint/theme/icons2/user.svg?jsx';
import ItemConditionIcon from '@tradera/blueprint/theme/icons2/condition.svg?jsx';
import PriceIcon from '@tradera/blueprint/theme/icons2/price.svg?jsx';
import ShippingIcon from '@tradera/blueprint/theme/icons2/shipping.svg?jsx';

const SearchFilter = ({
    filters,
    filterCategories,
    filterPrice = {},
    attributeFilters = [],
    handleCategoryChange,
    handleSearchCategoryChange,
    handleFilterChange,
    handlePriceFilterChange,
    handleAttributeFilterChange,
    pageType,
    queryParams,
    attributeFiltersEnabled,
    allowedBuyerRegionFilterEnabled
}) => {
    const { t } = useTranslator();
    const {
        itemCondition,
        sellerType,
        itemStatus,
        county,
        itemType,
        allowedBuyerRegions
    } = filters;
    const { fromPrice = {}, toPrice = {} } = filterPrice;
    return (
        <>
            <Accordion title={'Filters'}>
                {!filterCategories || !filterCategories.categoryTree ? null : (
                    <AccordionItem
                        title={t('searchFilters_categoriesTitle')}
                        defaultOpen
                        collapsable={false}
                        hideTitle={true}
                        className={'d-none d-md-block pb-2 bg-white'}
                        render={() => (
                            <FilterCategories
                                filterCategories={filterCategories}
                                handleCategoryChange={handleCategoryChange}
                                handleSearchCategoryChange={
                                    handleSearchCategoryChange
                                }
                                pageType={pageType}
                                queryParams={queryParams}
                            />
                        )}
                    />
                )}
                {attributeFiltersEnabled &&
                    attributeFilters.map(attributeFilter => (
                        <AccordionItem
                            key={attributeFilter.parameter}
                            title={getTranslatedAttributeName(
                                attributeFilter.parameter,
                                t
                            )}
                            icon={getAttributeIcon(attributeFilter.parameter)}
                            defaultOpen
                            render={() => (
                                <AttributeFilter
                                    attributeFilter={attributeFilter}
                                    handleAttributeFilterChange={
                                        handleAttributeFilterChange
                                    }
                                    queryParams={queryParams}
                                    pageType={pageType}
                                />
                            )}
                        />
                    ))}
                <AccordionItem
                    title={t('searchFilters_itemStatusTitle')}
                    icon={<ItemStatusIcon className="icon" />}
                    render={() => (
                        <RadioSwitchFilter
                            filter={itemStatus}
                            handleFilterChange={handleFilterChange}
                            id="filter-itemStatus"
                            className="form-control-radio-switch-sm"
                        />
                    )}
                />
                <AccordionItem
                    title={t('searchFilters_priceTitle')}
                    icon={<PriceIcon className="icon" />}
                    render={() => (
                        <PriceRange
                            rangeStart={1}
                            rangeEnd={5000}
                            handleStart={fromPrice.value || 1}
                            handleEnd={toPrice.value || 5000}
                            handlePriceFilterChange={handlePriceFilterChange}
                        />
                    )}
                />
                <AccordionItem
                    title={t('searchFilters_itemTypeTitle')}
                    icon={<ItemTypeIcon className="icon" />}
                    render={() => (
                        <RadioSwitchFilter
                            filter={itemType}
                            handleFilterChange={handleFilterChange}
                            id="filter-itemType"
                            className="form-control-radio-switch-sm"
                        />
                    )}
                />
                <AccordionItem
                    title={t('searchFilters_conditionTitle')}
                    icon={<ItemConditionIcon className="icon" />}
                    render={() => (
                        <RadioSwitchFilter
                            filter={itemCondition}
                            handleFilterChange={handleFilterChange}
                            id="filter-itemCondition"
                            className="form-control-radio-switch-sm"
                        />
                    )}
                />
                <AccordionItem
                    title={t('searchFilters_sellerTypeTitle')}
                    icon={<SellerTypeIcon className="icon" />}
                    render={() => (
                        <RadioSwitchFilter
                            filter={sellerType}
                            handleFilterChange={handleFilterChange}
                            id="filter-sellerType"
                            className="form-control-radio-switch-sm"
                        />
                    )}
                />
                <AccordionItem
                    title={t('searchFilters_shipToTitle')}
                    icon={<ShippingIcon className="icon" />}
                    render={() => (
                        <FilterShipTo
                            allowedBuyerRegions={allowedBuyerRegions}
                            county={county}
                            handleFilterChange={handleFilterChange}
                            queryParams={queryParams}
                            allowedBuyerRegionFilterEnabled={
                                allowedBuyerRegionFilterEnabled
                            }
                        />
                    )}
                />
            </Accordion>
        </>
    );
};

SearchFilter.propTypes = {
    filterCategories: PropTypes.object,
    filterPrice: PropTypes.object,
    filters: PropTypes.object,
    handleCategoryChange: PropTypes.func,
    handleFilterChange: PropTypes.func,
    handlePriceFilterChange: PropTypes.func,
    handleSearchCategoryChange: PropTypes.func,
    pageType: PropTypes.string,
    queryParams: PropTypes.object
};

export default SearchFilter;
